import { FC, useEffect, useMemo } from "react";
import { Circle, MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { getLeafletTileConfig } from "@/constants/leafletMapTiles";
import styles from "./InspectionLocationMap.module.scss";

type LatLng = { lat: number; lng: number };

type InspectionLocationMapProps = {
    registered?: LatLng | null;
    current?: LatLng | null;
    currentAccuracyMetres?: number | null;
};

function FitToMarkers({
    points,
}: {
    points: LatLng[];
}) {
    const map = useMap();
    useEffect(() => {
        if (points.length === 0) return;
        if (points.length === 1) {
            map.setView(points[0], 16, { animate: false });
            return;
        }
        const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
        map.fitBounds(bounds, { padding: [18, 18], animate: false });
    }, [map, points]);
    return null;
}

const registeredIcon = new L.DivIcon({
    className: "",
    html: `<div style="
        width:14px;height:14px;border-radius:50%;
        background:#2563eb;border:2px solid #ffffff;
        box-shadow:0 2px 10px rgba(0,0,0,.25);
    "></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
});

const currentIcon = new L.DivIcon({
    className: "",
    html: `<div style="
        width:14px;height:14px;border-radius:50%;
        background:#dc2626;border:2px solid #ffffff;
        box-shadow:0 2px 10px rgba(0,0,0,.25);
    "></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
});

export const InspectionLocationMap: FC<InspectionLocationMapProps> = ({
    registered,
    current,
    currentAccuracyMetres,
}) => {
    const points = useMemo(() => {
        const p: LatLng[] = [];
        if (registered) p.push(registered);
        if (current) p.push(current);
        return p;
    }, [registered, current]);

    const tile = useMemo(() => getLeafletTileConfig(), []);

    if (!registered && !current) return null;

    return (
        <div className={styles.mapWrap}>
            <MapContainer
                style={{ height: "100%", width: "100%" }}
                center={current ?? registered ?? { lat: 0, lng: 0 }}
                zoom={16}
                scrollWheelZoom={false}
                dragging
            >
                <TileLayer
                    attribution={tile.attribution}
                    url={tile.url}
                    maxZoom={20}
                    {...(tile.subdomains ? { subdomains: tile.subdomains } : {})}
                />

                <FitToMarkers points={points} />

                {registered && (
                    <Marker position={registered} icon={registeredIcon}>
                        <Popup>Registered location</Popup>
                    </Marker>
                )}

                {current && (
                    <>
                        <Marker position={current} icon={currentIcon}>
                            <Popup>Current device location</Popup>
                        </Marker>
                        {typeof currentAccuracyMetres === "number" &&
                            Number.isFinite(currentAccuracyMetres) &&
                            currentAccuracyMetres > 0 && (
                                <Circle
                                    center={current}
                                    radius={currentAccuracyMetres}
                                    pathOptions={{
                                        color: "#dc2626",
                                        weight: 1,
                                        fillColor: "#dc2626",
                                        fillOpacity: 0.12,
                                    }}
                                />
                            )}
                    </>
                )}
            </MapContainer>
        </div>
    );
};

